import React, { useRef, useState } from "react";
import { validate as validateEmail } from "email-validator";

// STYLES
import "./ForgotPassword.scss";

// COMPONENTS
import GenericButton from "../../ui/buttons/GenericButtons/GenericButton";
import LoadingSpinner from "../../ui/modals/Spinners/LoadingSpinner";
import GenericModal from "../../ui/modals/GenericModal/GenericModal";

// HOOKS
import { useAuthContext } from "../../../hooks/context/AuthContext";

interface IForgotPasswordEnterEmailProps {
    setUserEmail: (email: string) => void;
    onCodeSendSuccess: (updateRequired: boolean) => void;
    onCancel: () => void;
    onLegacyUserDetected: () => void;
}

const ForgotPassword: React.FC<IForgotPasswordEnterEmailProps> = ({
    setUserEmail,
    onCodeSendSuccess,
    onCancel,
    onLegacyUserDetected,
}) => {
    const [showLoadingSpinner, setShowLoadingSpinner] = useState(false);
    const [showCodeModal, setShowCodeModal] = useState(false);
    const [invalidEmailText, setInvalidEmailText] = useState("");

    const emailRef = useRef<HTMLInputElement | null>(null);

    const { postForgotPasswordAndSendCode } = useAuthContext();

    const validateAndSend = () => {
        const onSendSuccess = (email: string) => () => {
            setShowLoadingSpinner(false);
            setShowCodeModal(true);
            setInvalidEmailText("");
        };
        const onSendFail = (message: string) => {
            setShowLoadingSpinner(false);
            if (message === "migration") {
                onLegacyUserDetected();
            } else {
                setInvalidEmailText("Something went wrong, please try again.");
            }
        };

        const email = emailRef?.current?.value;
        if (email) {
            const emailValid = validateEmail(email);
            if (emailValid) {
                setShowLoadingSpinner(true);
                setInvalidEmailText("");
                setUserEmail(email);
                postForgotPasswordAndSendCode(
                    email,
                    onSendSuccess(email),
                    onSendFail,
                );
                return true;
            }
            setInvalidEmailText("Invalid Email");
        }
        setInvalidEmailText(
            "This field is required and must be a valid email address.",
        );
    };

    return (
        <div className="ForgotPassword">
            <LoadingSpinner
                className="ForgotPassword__OpaqueSpinner"
                raised={showLoadingSpinner}
            />
            <GenericModal
                className="ForgotPassword__CodeModal"
                show={showCodeModal}
                onHide={() => setShowCodeModal(false)}
                header="Code Sent!"
                body={
                    <p>
                        Check your email for your unique code and come back here
                        to reset your password
                    </p>
                }
                footer={
                    <GenericButton
                        className="ForgotPassword__Ok"
                        onClick={() => onCodeSendSuccess(false)}
                        text="OK"
                    />
                }
            />
            <div className="ForgotPassword__Wrapper">
                <div className="ForgotPassword__Header">
                    <h2>Forgot Password?</h2>
                    <p>
                        It happens! Enter your email address and we'll send you
                        a password reset code.
                    </p>
                </div>
                <div className="ForgotPassword__Body">
                    <h5>Email</h5>
                    <input
                        type="email"
                        ref={emailRef}
                        placeholder="Email"
                        onKeyUp={(e: React.KeyboardEvent<HTMLInputElement>) => {
                            if (e.key === "Enter") {
                                validateAndSend();
                            }
                        }}
                    />
                    <p className="ForgotPassword__BodyError">
                        {invalidEmailText}
                    </p>
                </div>
                <div className="ForgotPassword__Footer">
                    <GenericButton
                        text="Send Reset Code"
                        onClick={() => {
                            validateAndSend();
                        }}
                    />
                    <GenericButton
                        className="ForgotPassword__Cancel"
                        text="Cancel"
                        onClick={() => {
                            onCancel();
                        }}
                    />
                </div>
            </div>
        </div>
    );
};

export default ForgotPassword;
